import { useCallback, useEffect, useState, type FormEvent } from "react";

import {
  api,
  type ScheduledActivity,
  type CalendarColor,
  type Label as LabelData,
  type Routine,
  type RoutineStart,
  type Settings,
  type Template,
  type TemplateLibrary,
  type Today,
} from "@/api";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  DayChart,
  type DayChartSlice,
  type IdealSlice,
} from "@/components/day-chart";
import { ErrorAlert } from "@/components/error-alert";
import { LibraryBoard } from "@/components/library-board";
import { PageSkeleton } from "@/components/page-skeleton";
import { PlanningWizard } from "@/components/planning-wizard";
import { DraggableTemplateCard } from "@/components/template-card";
import { StartRoutineDialog } from "@/components/routine-start";
import { RoutineTimer } from "@/components/routine-timer";
import { loadRun, saveRun, type RoutineRun } from "@/lib/routine-run";
import { ErrorDialog } from "@/components/error-dialog";
import { ActivityStepsDialog } from "@/components/activity-steps-dialog";
import { Timeline, type TemplateDragPayload } from "@/components/timeline";
import { formatMinutes } from "@/lib/color";
import { labelHex } from "@/lib/domain";

function messageOf(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}

function clockToMinutes(hhmm: string): number {
  const [h, m] = hhmm.split(":").map(Number);
  return (h || 0) * 60 + (m || 0);
}

/** Today at HH:MM. */
function todayAt(hhmm: string): Date {
  const d = new Date();
  d.setHours(
    Math.floor(clockToMinutes(hhmm) / 60),
    clockToMinutes(hhmm) % 60,
    0,
    0,
  );
  return d;
}

/** "6 h 24 min–7 h 12 min" for a band, or one figure when the bounds meet. */
function targetBand(t: Today): string {
  return t.target_min_minutes === t.target_load_minutes
    ? formatMinutes(t.target_load_minutes)
    : `${formatMinutes(t.target_min_minutes)}–${formatMinutes(t.target_load_minutes)}`;
}

export function TodayPage() {
  const [today, setToday] = useState<Today | null>(null);
  const [library, setLibrary] = useState<TemplateLibrary | null>(null);
  const [settings, setSettings] = useState<Settings | null>(null);
  const [colors, setColors] = useState<CalendarColor[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [scheduling, setScheduling] = useState(false);
  const [planning, setPlanning] = useState(false);
  const [missingTime, setMissingTime] = useState<Template | null>(null);
  const [refining, setRefining] = useState<ScheduledActivity | null>(null);
  const [routines, setRoutines] = useState<Routine[]>([]);
  const [starting, setStarting] = useState<Routine | null>(null);
  const [run, setRun] = useState<RoutineRun | null>(() => loadRun());
  const [timerOpen, setTimerOpen] = useState(() => loadRun() !== null);
  const [notice, setNotice] = useState<string | null>(null);
  const [chore, setChore] = useState("");
  const [addingChore, setAddingChore] = useState(false);

  const load = useCallback(async () => {
    try {
      const [t, lib, s, r] = await Promise.all([
        api.today(),
        api.templates(),
        api.settings(),
        api.routines(),
      ]);
      setToday(t);
      setLibrary(lib);
      setSettings(s);
      setRoutines(r.routines);
      setError(null);
    } catch (err) {
      setError(messageOf(err, "could not load Today"));
    }
  }, []);

  useEffect(() => {
    void load();
    (async () => {
      try {
        setColors((await api.colors()).colors);
      } catch {
        // Colours are decoration; blocks fall back to the calendar's own colour.
      }
    })();
  }, [load]);

  /** An ended block is assumed done; if the person says it was not, move it
   *  to the next free slot later today. With no slot left the server marks it
   *  Missed instead. */
  async function handlePast(act: ScheduledActivity) {
    if (
      !window.confirm(
        `Reschedule “${act.title}” to the next free slot later today?`,
      )
    )
      return;
    setActionError(null);
    setNotice(null);
    try {
      const res = await api.rescheduleActivity(act.id);
      if (res.no_slot)
        setNotice(`No free slot left today: “${act.title}” is marked Missed.`);
      else {
        const at = res.start_at
          ? new Date(res.start_at).toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            })
          : "the next free slot";
        setNotice(`Moved “${act.title}” to ${at}.`);
      }
    } catch (err) {
      setActionError(messageOf(err, "could not reschedule"));
    }
    await load();
  }

  async function schedule(template: Template, labelId: string, start: Date) {
    setScheduling(true);
    setActionError(null);
    try {
      await api.scheduleActivity({
        template_id: template.id,
        display_label_id: labelId,
        start_at: start.toISOString(),
      });
      await load();
    } catch (err) {
      setActionError(messageOf(err, "could not schedule"));
    }
    setScheduling(false);
  }

  async function toggleVacation(vacation: boolean) {
    if (!today) return;
    setActionError(null);
    try {
      await api.setDayVacation(today.date, vacation);
      await load();
    } catch (err) {
      setActionError(messageOf(err, "could not update the vacation day"));
    }
  }

  async function addChore(ev: FormEvent<HTMLFormElement>) {
    ev.preventDefault();
    const title = chore.trim();
    if (!title) return;
    setAddingChore(true);
    setActionError(null);
    try {
      await api.quickChore(title);
      setChore("");
      await load();
    } catch (err) {
      setActionError(messageOf(err, "could not add the chore"));
    }
    setAddingChore(false);
  }

  function beginRun(result: RoutineStart) {
    const now = Date.now();
    const next: RoutineRun = {
      activityId: result.activity_id,
      routineTitle: result.title,
      items: result.chosen,
      index: 0,
      itemStartedAt: now,
      pausedAt: null,
      startedAt: now,
    };
    saveRun(next);
    setRun(next);
    setTimerOpen(true);
  }

  async function finishRun() {
    if (!run) return;
    await api.resolveActivity(run.activityId, "done");
    saveRun(null);
    setRun(null);
    setTimerOpen(false);
    setNotice(`${run.routineTitle} done. Nice work.`);
    await load();
  }

  function stopRun() {
    saveRun(null);
    setRun(null);
    setTimerOpen(false);
  }

  if (error && !today) return <ErrorAlert message={error} />;
  if (!today || !library || !settings) return <PageSkeleton />;

  const labels: LabelData[] = settings.labels;
  const labelById = new Map(labels.map((l) => [l.id, l]));
  // Same order as Settings (and the Ideal Day editor), so the planned share
  // lines up with the Ideal Day share Label by Label; unlabelled time last.
  const position = new Map(labels.map((l, i) => [l.id, i]));
  const slices: DayChartSlice[] = [...today.by_label]
    .sort(
      (a, b) =>
        (position.get(a.label_id) ?? Number.MAX_SAFE_INTEGER) -
        (position.get(b.label_id) ?? Number.MAX_SAFE_INTEGER),
    )
    .map((b) => {
      const label = labelById.get(b.label_id);
      return {
        id: b.label_id || "unlabelled",
        name: label?.name ?? "Not labeled",
        hex: label ? labelHex(colors, label) : undefined,
        minutes: b.minutes,
      };
    });
  const repeating = library.templates.filter((t) => t.type === "repeating");
  const tracked = repeating.filter((t) => t.track_streak);
  const activeStreaks = tracked.filter((t) => t.streak > 0);

  // Ideal Day comparison (numeric Mix: full duration under every Label).
  const labelledMix = today.mix.filter((m) => m.label_id !== "");
  const mixTotal = labelledMix.reduce((a, m) => a + m.minutes, 0);
  const ideal: IdealSlice[] = labels
    .filter((l) => l.system_role !== "external")
    .filter(
      (l) =>
        l.ideal_percent > 0 || labelledMix.some((m) => m.label_id === l.id),
    )
    .map((l) => {
      const minutes =
        labelledMix.find((m) => m.label_id === l.id)?.minutes ?? 0;
      return {
        id: l.id,
        name: l.name,
        hex: labelHex(colors, l),
        idealPercent: l.ideal_percent,
        actualPercent:
          mixTotal > 0 ? Math.round((minutes / mixTotal) * 100) : 0,
      };
    });
  const hasIdeal = labels.some((l) => l.ideal_percent > 0);
  const capacity = today.over_planned
    ? today.planned_minutes
    : today.target_load_minutes;
  const sharePct = (minutes: number) =>
    capacity > 0 ? Math.round((minutes / capacity) * 100) : 0;

  return (
    <div className="grid gap-4">
      {planning && (
        <PlanningWizard
          labels={labels}
          colors={colors}
          energyLevels={settings.energy_levels}
          todayEnergyLevelId={today.day_plan.energy_level_id}
          onClose={() => setPlanning(false)}
          onFinished={async () => {
            setPlanning(false);
            await load();
          }}
        />
      )}
      {missingTime && (
        <ErrorDialog
          title="No suggested time"
          message={`“${missingTime.title}” has no suggested time. Set one on the Template, or drag it onto the Calendar.`}
          onClose={() => setMissingTime(null)}
        />
      )}
      {refining && (
        <ActivityStepsDialog
          activity={refining}
          onClose={() => setRefining(null)}
          onSaved={async () => {
            setRefining(null);
            await load();
          }}
        />
      )}
      {starting && (
        <StartRoutineDialog
          routine={starting}
          onClose={() => setStarting(null)}
          onStarted={async (result) => {
            setStarting(null);
            setNotice(null);
            beginRun(result);
            await load();
          }}
        />
      )}
      {run && timerOpen && (
        <RoutineTimer
          run={run}
          onChange={setRun}
          onFinished={finishRun}
          onStop={stopRun}
        />
      )}
      {error && <ErrorAlert message={error} />}
      {actionError && <ErrorAlert message={actionError} />}
      {notice && (
        <p className="text-sm text-muted-foreground" role="status">
          {notice}
        </p>
      )}
      {run && !timerOpen && (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-primary/40 bg-secondary/40 px-3 py-2 text-sm">
          <span className="min-w-0 flex-1">
            <span className="font-medium">{run.routineTitle}</span> is running.
          </span>
          <Button size="sm" onClick={() => setTimerOpen(true)}>
            Resume timer
          </Button>
          <Button size="sm" variant="ghost" onClick={stopRun}>
            Stop
          </Button>
        </div>
      )}

      <Card>
        <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle>Day chart</CardTitle>
            <CardDescription>
              {today.day_plan.is_vacation
                ? "Vacation day: no planning, no efficiency calculated."
                : today.energy_level
                  ? `${today.energy_level.name} day, ${today.energy_level.range} of ${formatMinutes(today.waking_minutes)} waking.`
                  : "No Energy Level yet. Add one in Settings to set today's capacity."}
              {today.energy_level && !today.day_plan.energy_level_id
                ? " Using the default; run a Planning Session to choose today's."
                : ""}
              {today.margin_minutes > 0 && today.margin_total_minutes > 0
                ? ` Planned time includes a ${today.margin_minutes} min margin after each block (${formatMinutes(today.margin_total_minutes)} in total).`
                : ""}
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => void toggleVacation(!today.day_plan.is_vacation)}
            >
              {today.day_plan.is_vacation
                ? "Cancel vacation day"
                : "Vacation day"}
            </Button>
            {!today.day_plan.is_vacation && (
              <Button onClick={() => setPlanning(true)}>Plan my day</Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {today.over_planned && today.target_load_minutes > 0 && (
            <Alert variant="destructive">
              <AlertDescription>
                Over planning: {formatMinutes(today.planned_minutes)} planned
                against a {targetBand(today)} Target Load.
              </AlertDescription>
            </Alert>
          )}
          {today.target_load_minutes > 0 &&
            today.capacity_status === "in_band" && (
              <p className="text-sm text-muted-foreground">
                Well planned: {formatMinutes(today.planned_minutes)} is inside
                the {targetBand(today)} Target Load.
              </p>
            )}
          {today.target_load_minutes > 0 &&
            today.capacity_status === "under" && (
              <p className="text-sm text-muted-foreground">
                Room for {formatMinutes(today.room_minutes)} before the{" "}
                {targetBand(today)} Target Load.
              </p>
            )}
          <div className="flex flex-wrap items-center gap-6">
            <div className="w-36 shrink-0">
              <DayChart
                slices={slices}
                targetMinutes={today.target_load_minutes}
                targetMinMinutes={today.target_min_minutes}
                plannedMinutes={today.planned_minutes}
                ideal={hasIdeal ? ideal : undefined}
              />
            </div>
            <div className="min-w-56 flex-1">
              <p className="text-xs font-medium text-muted-foreground">
                Planned by Label
              </p>
              <ul className="mt-1 space-y-1 text-sm">
                {slices.length === 0 && (
                  <li className="text-muted-foreground">
                    Nothing planned inside your waking day yet.
                  </li>
                )}
                {slices.map((s) => (
                  <li key={s.id} className="flex items-center gap-2">
                    <span
                      className="h-2.5 w-2.5 shrink-0 rounded-full bg-muted-foreground"
                      style={s.hex ? { backgroundColor: s.hex } : undefined}
                      aria-hidden
                    />
                    <span className="min-w-0 flex-1 break-words">{s.name}</span>
                    <span
                      className="shrink-0 tabular-nums"
                      title={formatMinutes(s.minutes)}
                    >
                      {sharePct(s.minutes)}%
                    </span>
                  </li>
                ))}
                {today.target_load_minutes > 0 && !today.over_planned && (
                  <li className="flex items-center gap-2 text-muted-foreground">
                    <span
                      className="h-2.5 w-2.5 shrink-0 rounded-full bg-muted"
                      aria-hidden
                    />
                    <span className="min-w-0 flex-1">Unplanned</span>
                    <span
                      className="shrink-0 tabular-nums"
                      title={formatMinutes(
                        today.target_load_minutes - today.planned_minutes,
                      )}
                    >
                      {sharePct(
                        today.target_load_minutes - today.planned_minutes,
                      )}
                      %
                    </span>
                  </li>
                )}
              </ul>
            </div>
            <div className="min-w-48 flex-1">
              <p className="text-xs font-medium text-muted-foreground">
                Streaks
              </p>
              {tracked.length === 0 ? (
                <p className="mt-1 text-sm text-muted-foreground">
                  {repeating.length === 0
                    ? "No Repeating Templates yet."
                    : "No Repeating Template tracks a streak."}
                </p>
              ) : activeStreaks.length === 0 ? (
                <p className="mt-1 text-sm text-muted-foreground">
                  {tracked.length} tracked Repeating Template
                  {tracked.length === 1 ? "" : "s"}, no streaks yet. A streak
                  starts when one is marked Done in a Planning Session.
                </p>
              ) : (
                <ul className="mt-1 flex flex-wrap gap-2">
                  {activeStreaks.map((r) => (
                    <li key={r.id}>
                      <Badge
                        variant="outline"
                        title={`${r.title}: streak ${r.streak} of 5`}
                      >
                        {r.title} · {r.streak}/5
                      </Badge>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="min-w-48 flex-1">
              <p className="text-xs font-medium text-muted-foreground">
                Routines
              </p>
              {routines.length === 0 ? (
                <p className="mt-1 text-sm text-muted-foreground">
                  None yet. Define one under Routines.
                </p>
              ) : (
                <ul className="mt-1 flex flex-wrap gap-2">
                  {routines.map((r) => (
                    <li
                      key={r.id}
                      className="flex items-center gap-2 rounded-full border border-border bg-card py-1 pr-1 pl-3 text-sm"
                    >
                      <span className="min-w-0 truncate">
                        <span className="font-medium">{r.title}</span>
                        <span className="text-muted-foreground">
                          {" "}
                          · {formatMinutes(r.total_minutes)}
                        </span>
                      </span>
                      <Button
                        size="sm"
                        className="h-7 px-3 text-xs"
                        onClick={() => setStarting(r)}
                        disabled={run !== null}
                        title={`Start ${r.title}: tick the actions to do now; they become one calendar block`}
                      >
                        Start
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid min-w-0 gap-4 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)] lg:items-start">
        <div className="flex min-w-0 flex-col gap-4">
          <Card className="min-w-0">
            <CardHeader>
              <CardTitle>Templates</CardTitle>
              <CardDescription>
                Drag onto the Calendar, or double-click to schedule at the
                suggested time. The column you drag from sets the colour.
              </CardDescription>
            </CardHeader>
            <CardContent className="min-w-0">
              {labels.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  Create Labels and Templates first.
                </p>
              ) : (
                <LibraryBoard
                  library={library}
                  labels={labels}
                  colors={colors}
                  renderCard={(t, label, hex) => (
                    <DraggableTemplateCard
                      template={t}
                      labelId={label.id}
                      accent={hex}
                      disabled={scheduling}
                      title="Drag onto the Calendar, or double-click to schedule"
                      onDoubleClick={() => {
                        if (!t.suggested_start) {
                          setMissingTime(t);
                          return;
                        }
                        void schedule(t, label.id, todayAt(t.suggested_start));
                      }}
                    />
                  )}
                />
              )}
            </CardContent>
          </Card>
        </div>

        <Card className="min-w-0">
          <CardHeader>
            <CardTitle>Calendar</CardTitle>
            <CardDescription>
              {today.day_start}–{today.day_end}, synced with Google Calendar (
              {today.time_zone}). Drop a Template to schedule it; change times
              on Google Calendar.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <form className="flex gap-2" onSubmit={(ev) => void addChore(ev)}>
              <Input
                aria-label="Quick chore"
                placeholder="Quick chore… starts now, 30 min"
                value={chore}
                onChange={(e) => setChore(e.target.value)}
                disabled={addingChore}
              />
              <Button
                type="submit"
                variant="outline"
                disabled={addingChore || chore.trim() === ""}
              >
                Add
              </Button>
            </form>
            <Timeline
              date={today.date}
              dayStartMinutes={clockToMinutes(today.day_start)}
              dayEndMinutes={clockToMinutes(today.day_end)}
              activities={today.activities}
              linksByTemplate={
                new Map(library.templates.map((t) => [t.id, t.links]))
              }
              onPastClick={(a) => void handlePast(a)}
              onFutureClick={setRefining}
              onDropTemplate={(start, payload: TemplateDragPayload) => {
                const t = library.templates.find(
                  (x) => x.id === payload.template_id,
                );
                if (t) void schedule(t, payload.label_id, start);
              }}
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
